import {
  collection,
  doc,
  writeBatch,
  type CollectionReference,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore';

// 求人票の年度別サブコレクションの置き場所を1か所に集約する。
//
// 親は素の companyId ではなく、既存のフラットドキュメント {companyId}_2027。
// こうすると Firestore コンソール上で、求人(フラットのフィールド)と
// 求人票(jobPostings サブコレクション)が同じドキュメント配下に並んで見える。
// "_2027" は現状 recruits コレクション全件で固定のサフィックスであり、
// 年度パーティションとしては機能していない(= 実質ただの定数)。
// サブコレクション側の子ドキュメントIDが「卒業年度」("2027" / "2028" ...)。
//
// 中途採用(17.midCareer)は別のフラットドキュメント
// {companyId}_midCareer_{position} を親に持つため、このヘルパーの対象外。
export const LEGACY_JOB_POSTING_YEAR = '2027';
const LEGACY_RECRUIT_SUFFIX = `_${LEGACY_JOB_POSTING_YEAR}`;

export const jobPostingParentId = (companyId: string): string =>
  `${companyId}${LEGACY_RECRUIT_SUFFIX}`;

// 求人票を作成・保存・送付できる卒業年度(年度一覧に並ぶ年度)。
// URL の :year はこの一覧に含まれるものだけを受け付ける(任意の年度ドキュメントを作らせない)。
// 年度を増やすときは、ここと firestore.rules の recruits/{recruitId}/jobPostings/{year}、
// functions/jobPostings.js の SUPPORTED_JOB_POSTING_YEARS を合わせて更新する。
export const SUPPORTED_JOB_POSTING_YEARS = ['2027', '2028'] as const;

export const isSupportedJobPostingYear = (year: string | undefined): year is string =>
  !!year && (SUPPORTED_JOB_POSTING_YEARS as readonly string[]).includes(year);

// 新規オンボーディング(1.manual/3.school)が対象とする求人票の卒業年度。
// アンカードキュメントのID(上記 _2027 固定)とは無関係で、こちらは
// 「今から新規登録する会社の最初の求人票をどの年度向けに作るか」だけを表す。
// 来年度以降は、この値を更新するだけで新規会社の対象年度を切り替えられる
// (LEGACY_JOB_POSTING_YEARは変更しないこと。company/求人ページ等15以上の
// 無関係な機能がアンカードキュメントIDとして_2027決め打ちで参照しているため)。
export const ONBOARDING_JOB_POSTING_YEAR = '2028';

// フラットドキュメントID "{companyId}_2027" から素の companyId を取り出す。
// 求人票専有フィールドをオンボーディング側(documentId しか持っていない)から
// 両ストアへ書くときに使う。
export const companyIdFromParentId = (parentDocId: string): string =>
  parentDocId.endsWith(LEGACY_RECRUIT_SUFFIX)
    ? parentDocId.slice(0, -LEGACY_RECRUIT_SUFFIX.length)
    : parentDocId;

// recruits/{companyId}_2027/jobPostings/{year}
export const jobPostingYearDoc = (
  db: Firestore,
  companyId: string,
  year: string,
): DocumentReference =>
  doc(db, 'recruits', jobPostingParentId(companyId), 'jobPostings', year);

// recruits/{companyId}_2027/jobPostings
export const jobPostingYearCollection = (
  db: Firestore,
  companyId: string,
): CollectionReference =>
  collection(db, 'recruits', jobPostingParentId(companyId), 'jobPostings');

// 求人票専有フィールド(info / target / employment20・18・17 / benefit /
// applicationInfo / hashTag / warning.* / jobPostingsStatus)を、
// フラットドキュメント recruits/{companyId}_2027 と年度別サブコレクション
// recruits/{companyId}_2027/jobPostings/{year} の「両方」へ同じ内容で書き込む。
//
//   - どちらの編集画面(オンボーディング 1.manual/3.school /
//     年度別 2.jobPostings/edit)から保存しても両ストアが同期されるようにするための
//     共通関数。以前はエディタごとに書き込み先が片方だけで、もう一方に反映されなかった。
//   - フラットドキュメントIDは "_2027" 固定サフィックスのため、2027年度以外
//     (2028年度〜)の内容をフラットへ書くと別年度の内容を上書きしてしまう。
//     そのため year !== '2027' のときはサブコレクションのみへ書く。
//   - サブコレクション側は未作成のことがあるので、先に空ドキュメントを merge:true で
//     用意してから updateDoc する(呼び出し側がフィールドパス記法 "a.b" や
//     deleteField() をそのまま渡せるようにするため。setDoc(merge) では "a.b" が
//     ネストではなくリテラルなキーとして書かれてしまう)。
//   - summary / store / pamphlet は 3.recruit(求人ページ)と共有データなので
//     この関数の対象外。従来どおりフラットドキュメントだけを読み書きする。
export const writeJobPostingBothStores = async (
  db: Firestore,
  companyId: string,
  year: string,
  data: Record<string, unknown>,
): Promise<void> => {
  const subRef = jobPostingYearDoc(db, companyId, year);

  // 1つのバッチでまとめて書き込み、2つの保存先の片方だけが更新されるのを防ぐ
  // (以前は Promise.all で別々に更新していたため、片方だけ失敗すると中身がずれた)。
  const batch = writeBatch(db);

  // サブコレクションドキュメントの存在を保証(merge なので既存は壊さない)。
  // 同じバッチ内の後続の update はこの set の後に適用される。
  batch.set(subRef, {}, { merge: true });
  batch.update(subRef, data as any);

  if (year === LEGACY_JOB_POSTING_YEAR) {
    batch.update(doc(db, 'recruits', jobPostingParentId(companyId)), data as any);
  }

  await batch.commit();
};
